import PropTypes from "prop-types";
import { Box, Typography, Paper, Grid, Divider, Chip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import { UPLOAD_TYPES } from "../constants";
import { formatDateTime } from "../../../../utils/dateFormatters-campaign.utils";
import { PricingSummary } from "..";

export const SummarySection = ({
  formData,
  selectedContacts,
  uploadedFile,
  templateName,
  pricing,
  loadingPricing,
  termsAccepted,
  onAcceptTerms,
  selectedCategories = [],
  importReport = null,
  templateVariables = [],
  validationResult = null,
}) => {
  const renderAudienceInfo = () => {
    if (formData.audienceType === UPLOAD_TYPES.CONTACTS) {
      return (
        <>
          <Typography variant="body2" color="text.secondary">
            Tipo de Audiencia
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Contactos seleccionados
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Total de Contactos
          </Typography>
          <Typography variant="body1">
            {selectedContacts.length} contacto
            {selectedContacts.length !== 1 ? "s" : ""}
          </Typography>
        </>
      );
    }

    if (formData.audienceType === UPLOAD_TYPES.FILE) {
      return (
        <>
          <Typography variant="body2" color="text.secondary">
            Tipo de Audiencia
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Archivo cargado
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Nombre del Archivo
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {uploadedFile?.name || "Sin archivo"}
          </Typography>

          {templateVariables.length > 0 && (
            <>
              <Typography variant="body2" color="text.secondary">
                Variables del Template
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                {templateVariables.map((v) => (
                  <Chip key={v} label={`{{${v}}}`} size="small" variant="outlined" />
                ))}
              </Box>
              {validationResult && (
                <Typography variant="body2" color="text.secondary">
                  Archivo validado con {validationResult.variableRows?.length || 0} filas de datos
                </Typography>
              )}
            </>
          )}

          {uploadedFile && (
            <>
              <Typography variant="body2" color="text.secondary">
                Tamaño
              </Typography>
              <Typography variant="body1">
                {(uploadedFile.size / 1024).toFixed(2)} KB
              </Typography>
            </>
          )}

          {importReport && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Contactos Válidos (MX + US)
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {(importReport.valid_mx + importReport.valid_us).toLocaleString()} contactos
              </Typography>

              {importReport.duplicates > 0 && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Duplicados eliminados
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {importReport.duplicates.toLocaleString()}
                  </Typography>
                </>
              )}

              {importReport.rejected > 0 && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Rechazados
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {importReport.rejected.toLocaleString()}
                  </Typography>
                </>
              )}
            </>
          )}
        </>
      );
    }

    if (formData.audienceType === UPLOAD_TYPES.CATEGORIES) {
      return (
        <>
          <Typography variant="body2" color="text.secondary">
            Tipo de Audiencia
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Segmentado por categorías
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Categorías seleccionadas
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1 }}>
            {selectedCategories.map((cat) => (
              <Chip
                key={cat?.name || cat}
                label={cat?.name || cat}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>

          <Typography variant="body2" color="text.secondary">
            Archivo generado
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {uploadedFile?.name || "Sin archivo"}
          </Typography>

          {uploadedFile && (
            <Typography variant="body2" color="text.secondary">
              Tamaño: {(uploadedFile.size / 1024).toFixed(2)} KB
            </Typography>
          )}
        </>
      );
    }

    return (
      <Typography variant="body1" color="text.secondary">
        No se seleccionó audiencia
      </Typography>
    );
  };

  const renderScheduleInfo = () => {
    if (!formData.scheduledDateTime) {
      return (
        <Typography variant="body1" color="text.secondary">
          No se seleccionó fecha
        </Typography>
      );
    }

    return (
      <>
        <Typography variant="body2" color="text.secondary">
          Fecha y Hora de Envío
        </Typography>
        <Typography variant="body1">
          {formatDateTime(formData.scheduledDateTime)}
        </Typography>
      </>
    );
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1,
        }}
      >
        <CheckCircleIcon color="success" sx={{ fontSize: 32 }} />
        <Typography variant="h6">Revisa los detalles de tu campaña</Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        <strong>Nota: </strong>
        Verifica que toda la información sea correcta antes de crear la campaña.
        Una vez creada la campaña, podrás visualizarla, editarla o lanzarla
        manualmente cuando lo desees desde la tabla de campañas.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: "grey.50",
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Datos Básicos
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Nombre de Campaña
                </Typography>
                <Typography variant="body1">
                  {formData.campaignName || "-"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Alias
                </Typography>
                <Typography variant="body1">{formData.alias || "-"}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Template
                </Typography>
                <Typography variant="body1">
                  {templateName || "Sin template"}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Descripción
                </Typography>
                <Typography variant="body1">
                  {formData.descripcion || "Sin descripción"}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: "grey.50",
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Audiencia
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                {renderAudienceInfo()}
                {importReport && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    <Chip
                      icon={<CheckCircleIcon />}
                      label={`${(importReport.valid_mx + importReport.valid_us).toLocaleString()} contactos válidos`}
                      color="success"
                      variant="outlined"
                      size="small"
                    />
                    {importReport.duplicates > 0 && (
                      <Chip
                        icon={<WarningAmberIcon />}
                        label={`${importReport.duplicates.toLocaleString()} duplicados`}
                        color="warning"
                        variant="outlined"
                        size="small"
                      />
                    )}
                    {importReport.rejected > 0 && (
                      <Chip
                        icon={<ErrorIcon />}
                        label={`${importReport.rejected.toLocaleString()} rechazados`}
                        color="error"
                        variant="outlined"
                        size="small"
                      />
                    )}
                    {importReport.estimated_templates_needed > 1 && (
                      <Chip
                        icon={<InfoIcon />}
                        label={`${importReport.estimated_templates_needed} templates necesarios`}
                        color="info"
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>


        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              px: 2,
              backgroundColor: "grey.50",
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Programación
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                {renderScheduleInfo()}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <PricingSummary 
            pricing={pricing} 
            loading={loadingPricing} 
            termsAccepted={termsAccepted}
            onAcceptTerms={onAcceptTerms}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

SummarySection.propTypes = {
  formData: PropTypes.shape({
    campaignName: PropTypes.string,
    alias: PropTypes.string,
    template: PropTypes.string,
    descripcion: PropTypes.string,
    audienceType: PropTypes.string,
    scheduledDateTime: PropTypes.instanceOf(Date),
  }).isRequired,
  selectedContacts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      nombre: PropTypes.string,
      telefono: PropTypes.string,
    })
  ),
  uploadedFile: PropTypes.instanceOf(File),
  templateName: PropTypes.string,
  pricing: PropTypes.object,
  loadingPricing: PropTypes.bool,
  termsAccepted: PropTypes.bool,
  onAcceptTerms: PropTypes.func,
  selectedCategories: PropTypes.array,
  importReport: PropTypes.shape({
    valid_mx: PropTypes.number,
    valid_us: PropTypes.number,
    duplicates: PropTypes.number,
    rejected: PropTypes.number,
    rejected_file_url: PropTypes.string,
    estimated_templates_needed: PropTypes.number,
  }),
  templateVariables: PropTypes.array,
  validationResult: PropTypes.object,
};

SummarySection.defaultProps = {
  selectedContacts: [],
  uploadedFile: null,
  templateName: "",
  pricing: null,
  loadingPricing: false,
  termsAccepted: false,
  onAcceptTerms: () => {},
  selectedCategories: [],
  importReport: null,
  templateVariables: [],
  validationResult: null,
};
